import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';

import { FormlyFieldConfig } from '@ngx-formly/core';

import { take } from 'rxjs';

import { CoreService, DialogResult, DialogService, DspxBaseFormCommonComponent } from 'dspx-core';

import { NAV_TAB_NUMBERS, PAGE_NUMBERS, QUESTION_CODES, SECTION_CODES } from "../constants/app-data.constants";

import { FormConfigurationParserService } from './form-configuration-parser.service';
import { QuestionPath, ResponseParserService } from './response-parser.service';

export type HideableGridConfiguration = {
  showOnFirstLoad: boolean,
  buttonQuestionPaths: { show: QuestionPath, hide: QuestionPath },
  functionNames: { show: string, hide: string, shouldShow: string, shouldHide: string }
}

export type HideableGrid = {
  toggle: (show: boolean) => void,
}

@Injectable({
  providedIn: 'root'
})
export class FormDataService {
  appData: { [key: string]: any } = {};

  constructor(
    private coreService: CoreService,
    private formConfigurationParserService: FormConfigurationParserService,
    private responseParserService: ResponseParserService,
    private dialogService: DialogService) {
  }

  private findFieldValue<T>(questionConfigs: FormlyFieldConfig[], questionKey: string, defaultValue: T = null): T {
    const field = questionConfigs.find(f => f.key === questionKey);
    const value = (field && this.coreService.getFieldValue(field) as unknown as T) || defaultValue;
    return value;
  }

  private findFieldLabel(questionConfigs: FormlyFieldConfig[], questionKey: string, defaultValue: string = null): string {
    const field = questionConfigs.find(f => f.key === questionKey);
    const label = field?.templateOptions?.label || defaultValue;
    return label;
  }

  public showOkCancelPopup(message: string, header: string, onComplete: (cancel: boolean) => void): void {
    this.dialogService
      .showSimplePopup(message, header, 'OK', 'Cancel')
      .pipe(take(1))
      .subscribe((dialogResult: DialogResult) => onComplete(dialogResult === DialogResult.REJECTED));
  }

  configureReferenceDataFormViewMode(getFormConfig: () => FormlyFieldConfig[], isViewModeAllowed: () => boolean, setViewMode: (enabled: boolean) => void, getViewMode: () => boolean) {

    this.coreService.applicationFunctions['enableViewModeForCategory'] = () => setViewMode(true);
    this.coreService.applicationFunctions['disableViewModeForCategory'] = () => setViewMode(false);
    this.coreService.applicationFunctions['showEnableViewModeButtonForCategory'] = () => isViewModeAllowed() && !getViewMode();
    this.coreService.applicationFunctions['showDisableViewModeButtonForCategory'] = () => isViewModeAllowed() && getViewMode();
    this.coreService.applicationFunctions['alwaysFalse'] = () => false; // This ensures that all the other buttons have the same "secondary" styling

    this.coreService.pageFunctions['isViewModeForCategory'] =
      (pageConfig: FormlyFieldConfig) => isViewModeAllowed() && getViewMode();

    this.coreService.pageFunctions['isSectionIncludedInViewModeForCategory'] =
      (pageConfig: FormlyFieldConfig, sectionConfig: FormlyFieldConfig) => sectionConfig.key === SECTION_CODES.referenceDataForm.viewRefDataCategory;

    this.coreService.pageFunctions['getViewModeCustomHtmlForCategory'] =
      (pageConfig: FormlyFieldConfig, sectionConfig: FormlyFieldConfig, sectionIndex: number, questionConfigs: FormlyFieldConfig[]) => {
        const referenceCategoryQuestionConfigs = this.formConfigurationParserService.findQuestionConfigs(
            getFormConfig?.(),
            NAV_TAB_NUMBERS.referenceDataForm.refDataCategory,
            PAGE_NUMBERS.referenceDataForm.refDataCategory,
            SECTION_CODES.referenceDataForm.refDataCategory)
          ?? [];

        const code = this.findFieldValue<string>(referenceCategoryQuestionConfigs, QUESTION_CODES.referenceDataForm.referenceCategoryCode, '');
        const text = this.findFieldValue<string>(referenceCategoryQuestionConfigs, QUESTION_CODES.referenceDataForm.referenceCategoryText, '');
        const isActive = this.findFieldValue<boolean>(referenceCategoryQuestionConfigs, QUESTION_CODES.referenceDataForm.referenceCategoryIsActive, null);

        const description =
          [
            [code, text].filter(item => item).join(' - '),
            isActive ? `` : `(INACTIVE)`
          ]
            .filter(item => item)
            .join(' ');
        return isActive
          ? `<div class="p-col-12"><h1>${description}</h1></div>`
          : `<div class="p-col-12"><h1><i>${description}</i></h1></div>`;
      }
  }

  referenceDataFormIsEditingRefValue(model: any, sectionIndex: number): boolean {
    const idValue = this.responseParserService.findQuestionModel(
      model,
      NAV_TAB_NUMBERS.referenceDataForm.refDataValue,
      PAGE_NUMBERS.referenceDataForm.refDataValue,
      null,
      SECTION_CODES.referenceDataForm.refDataValue,
      sectionIndex,
      'id');
    return idValue !== null && idValue !== undefined;
  }

  // This is just a workaround to force the rendering of a control.
  forceRender(control: AbstractControl): void {
    if (control.dirty) {
      control.markAsPristine();
      control.markAsDirty();
    }
    else {
      control.markAsDirty();
      control.markAsPristine();
    }
  }

  reloadModel(baseForm: DspxBaseFormCommonComponent): void {
    baseForm.model = { ...baseForm.model };
  }

  initialiseHideableGrid(getBaseForm: () => DspxBaseFormCommonComponent, configuration: HideableGridConfiguration): HideableGrid {

    let isShown = configuration?.showOnFirstLoad;

    const toggle = (show: boolean) => {
      isShown = show;
      const visibleButton = show ? configuration?.buttonQuestionPaths?.hide : configuration?.buttonQuestionPaths?.show;
      this.reloadModel(getBaseForm());
      setTimeout(() => ensureButtonIsEnabled(visibleButton));
    };

    // This is needed to ensure that the currently visible button is forced to be enabled when the form is loaded as not editable.
    const ensureButtonIsEnabled = (buttonQuestionPath: QuestionPath) => {
      const buttonQuestionConfig = this.formConfigurationParserService.findQuestionConfig(getBaseForm().fields, ...buttonQuestionPath);
      const buttonQuestionControl = buttonQuestionConfig?.formControl;
      buttonQuestionControl?.enable();
    }

    this.coreService.conditionFunctions[configuration?.functionNames?.shouldShow] = () => isShown;
    this.coreService.conditionFunctions[configuration?.functionNames?.shouldHide] = () => !isShown;

    this.coreService.applicationFunctions[configuration?.functionNames?.show] = () => toggle(true);
    this.coreService.applicationFunctions[configuration?.functionNames?.hide] = () => toggle(false);

    return { toggle };
  }
}
